import React, { useEffect, useMemo, useState } from 'react';
import { Paper, TextField, Grid, Button, Alert, Box, Typography, IconButton, InputAdornment, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Add as AddIcon, Delete as DeleteIcon, Image as ImageIcon } from '@mui/icons-material';
import SitesService from '../../../services/SitesService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { useSitesConfig } from '../SitesContext';
import { prepareFieldUpdates } from '../utils/fieldUtils';
import ImageSelector from '../components/ImageSelector';

const isImagePath = (value) => value && typeof value === 'string' && !value.startsWith('http');

const HERO_FIELDS = [
  {
    path: 'siteConfig.hero.type',
    labelKey: 'hero_type',
    fallback: 'Tipo de Hero',
    select: true,
    options: [
      { value: 'text', labelKey: 'text', fallback: 'Texto' },
      { value: 'image', labelKey: 'section_hero_image', fallback: 'Imagen' },
      { value: 'carousel', labelKey: 'carousel', fallback: 'Carrusel' }
    ]
  },
  { path: 'siteConfig.hero.badgeText', labelKey: 'hero_badge_text', fallback: 'Texto del badge' },
  { path: 'siteConfig.hero.title', labelKey: 'hero_title', fallback: 'Título del Hero' },
  { path: 'siteConfig.hero.subtitle', labelKey: 'hero_subtitle', fallback: 'Subtítulo del Hero' },
  { path: 'siteConfig.hero.description', labelKey: 'hero_description', fallback: 'Descripción del Hero', multiline: true, rows: 3 },
  { path: 'siteConfig.hero.ctaText', labelKey: 'cta_text', fallback: 'Texto del CTA' },
  { path: 'siteConfig.hero.ctaLink', labelKey: 'cta_link', fallback: 'URL del CTA' },
  { path: 'siteConfig.hero.secondaryCtaText', labelKey: 'secondary_cta_text', fallback: 'Texto del CTA secundario' },
  { path: 'siteConfig.hero.secondaryCtaLink', labelKey: 'secondary_cta_link', fallback: 'URL del CTA secundario' },
  { path: 'siteConfig.hero.backgroundImage', labelKey: 'background_image', fallback: 'Imagen de fondo' }
];

const HeroTab = () => {
  const { t } = useTranslation();
  const { config, handleChange, isFieldConfigurable, isFieldFixed, loadConfig } = useSitesConfig();
  const [saving, setSaving] = useState(false);
  const [imageSelectorOpen, setImageSelectorOpen] = useState(false);
  const [imageSelectorMode, setImageSelectorMode] = useState(null);
  const [previewUrls, setPreviewUrls] = useState({});

  const getLabel = (key, fallback) => {
    const translated = t(key);
    return translated && translated !== key ? translated : fallback;
  };

  const fields = useMemo(
    () => HERO_FIELDS.filter((field) => isFieldConfigurable(field.path)),
    [isFieldConfigurable]
  );

  const backgroundImage = config?.siteConfig?.hero?.backgroundImage;
  const carouselImages = Array.isArray(config?.siteConfig?.hero?.carouselImages)
    ? config.siteConfig.hero.carouselImages
    : [];
  const canEditCarouselImages = isFieldConfigurable('siteConfig.hero.carouselImages') && !isFieldFixed('siteConfig.hero.carouselImages');
  const imagePaths = [backgroundImage, ...carouselImages].filter(isImagePath);
  const imagePathsKey = JSON.stringify(imagePaths);

  useEffect(() => {
    if (imagePaths.length === 0) {
      setPreviewUrls({});
      return;
    }

    let cancelled = false;
    const load = async () => {
      const next = {};
      for (const path of imagePaths) {
        if (cancelled) return;
        const url = await SitesService.getImagePreviewUrl(path);
        if (!cancelled && url) {
          next[path] = url;
        }
      }
      if (!cancelled) setPreviewUrls((prev) => ({ ...prev, ...next }));
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [imagePathsKey]);

  const getFieldValue = (path) => {
    const value = path.split('.').reduce((obj, key) => obj?.[key], config);
    return value !== null && value !== undefined ? value : '';
  };

  const getPreviewSrc = (value) => {
    if (!value) return null;
    if (!isImagePath(value)) return value;
    return previewUrls[value] || null;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const updates = {};

      fields.forEach((field) => {
        updates[field.path] = getFieldValue(field.path);
      });
      if (canEditCarouselImages) {
        updates['siteConfig.hero.carouselImages'] = carouselImages;
      }

      if (Object.keys(updates).length === 0) {
        SnackbarUtils.warning(t('no_editable_fields') || 'No hay campos editables en esta sección');
        return;
      }

      await SitesService.updateFields(prepareFieldUpdates(updates));
      await loadConfig();
      SnackbarUtils.success(t('section_saved_successfully') || 'Sección guardada exitosamente');
    } catch (err) {
      console.error('Error saving hero:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
      SnackbarUtils.error(errorMessage || (t('error_saving_section') || 'Error al guardar la sección'));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenImageSelector = (mode) => {
    setImageSelectorMode(mode);
    setImageSelectorOpen(true);
  };

  const handleCarouselImageSelect = (imageUrl) => {
    handleChange('siteConfig.hero.carouselImages', [...carouselImages, imageUrl]);
    const currentHeroType = getFieldValue('siteConfig.hero.type');
    if (!currentHeroType || currentHeroType === 'text') {
      handleChange('siteConfig.hero.type', 'carousel');
    }
  };

  const handleRemoveCarouselImage = (indexToRemove) => {
    handleChange(
      'siteConfig.hero.carouselImages',
      carouselImages.filter((_, index) => index !== indexToRemove)
    );
  };

  if (fields.length === 0 && !canEditCarouselImages) {
    return (
      <Paper sx={{ padding: 3, mt: 2 }}>
        <Alert severity="info">
          {t('no_editable_fields_in_section') || 'No hay campos editables en esta sección para el template actual'}
        </Alert>
      </Paper>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Paper sx={{ padding: 3, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('section_hero') || 'Hero'}
        </Typography>
        <Grid container spacing={2}>
          {fields.map((field) => {
            const value = getFieldValue(field.path);
            const isImageField = field.path === 'siteConfig.hero.backgroundImage';

            return (
              <Grid item xs={12} key={field.path}>
                <TextField
                  fullWidth
                  label={getLabel(field.labelKey, field.fallback)}
                  value={value}
                  onChange={(e) => handleChange(field.path, e.target.value)}
                  select={field.select}
                  multiline={field.multiline}
                  rows={field.rows || 1}
                  disabled={isFieldFixed(field.path)}
                  InputProps={isImageField ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleOpenImageSelector('background')}
                          edge="end"
                          color="primary"
                          disabled={isFieldFixed(field.path)}
                        >
                          <ImageIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  } : undefined}
                >
                  {field.select && field.options?.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {getLabel(option.labelKey, option.fallback)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            );
          })}

          {backgroundImage && (
            <Grid item xs={12}>
              <Box sx={{ mt: 1, textAlign: 'center' }}>
                <img
                  src={getPreviewSrc(backgroundImage) || backgroundImage}
                  alt="Hero preview"
                  style={{
                    width: '100%',
                    maxHeight: '220px',
                    objectFit: 'cover',
                    border: '1px solid #ddd',
                    borderRadius: '8px'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </Box>
            </Grid>
          )}

          {canEditCarouselImages && (
            <Grid item xs={12}>
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1">
                      {getLabel('carousel_images', 'Imágenes del Carrusel')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {getLabel('carousel_images_helper', 'Agrega las imágenes que se mostrarán en el carrusel')}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenImageSelector('carousel')}
                  >
                    {getLabel('add_image', 'Agregar Imagen')}
                  </Button>
                </Box>

                {carouselImages.length === 0 ? (
                  <Alert severity="info">
                    {t('no_images_found') || 'No hay imágenes cargadas para el carrusel'}
                  </Alert>
                ) : (
                  <Grid container spacing={2}>
                    {carouselImages.map((image, index) => (
                      <Grid item xs={12} sm={6} md={4} key={`${image}-${index}`}>
                        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                          <Box
                            component="img"
                            src={getPreviewSrc(image) || image}
                            alt={`Carousel ${index + 1}`}
                            sx={{ display: 'block', width: '100%', height: 140, objectFit: 'cover', bgcolor: 'grey.100' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1 }}>
                            <Typography variant="caption" noWrap sx={{ pr: 1 }}>
                              {image}
                            </Typography>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveCarouselImage(index)}
                              aria-label={t('delete') || 'Eliminar'}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
                {saving ? (t('saving') || 'Guardando...') : (t('save') || 'Guardar')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <ImageSelector
        open={imageSelectorOpen}
        onClose={() => {
          setImageSelectorOpen(false);
          setImageSelectorMode(null);
        }}
        onSelect={(imageUrl) => {
          if (imageSelectorMode === 'carousel') {
            handleCarouselImageSelect(imageUrl);
          } else {
            handleChange('siteConfig.hero.backgroundImage', imageUrl);
            const currentHeroType = getFieldValue('siteConfig.hero.type');
            if (!currentHeroType || currentHeroType === 'text') {
              handleChange('siteConfig.hero.type', 'image');
            }
          }
          setImageSelectorOpen(false);
          setImageSelectorMode(null);
        }}
        currentValue={getFieldValue('siteConfig.hero.backgroundImage')}
        label={imageSelectorMode === 'carousel'
          ? getLabel('carousel_images', 'Imágenes del Carrusel')
          : getLabel('background_image', 'Imagen de fondo')}
      />
    </Box>
  );
};

export default HeroTab;
